import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { Subject, take, takeUntil } from 'rxjs';

import { AppUtilityService } from '../../../shared/services/app-utility.service';
import { apiPaths } from '../../core/constants/api.const';
import { CommonDialogComponent } from '../../../shared/components/common-dialog/common-dialog.component';
import { AddUserDialogComponent, AddUserPayload } from './add-user-dialog/add-user-dialog.component';
import { EditUserDialogComponent, EditUserPayload } from './edit-user-dialog/edit-user-dialog.component';
import { selectAppState } from '../../state/selectors';

interface UserInfo {
  first_name: string;
  last_name: string;
  email: string;
  employee_id?: string;
  job_title: string;
  super_user: boolean;
  is_active: boolean;
}

interface UsersResponse {
  users: UserInfo[];
}

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['first_name', 'last_name', 'email', 'job_title'];
  dataSource = new MatTableDataSource<UserInfo>([]);
  selectedUser: UserInfo | null = null;
  loggedInUserName = '';
  loggedInUserEmail = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private appUtilityService: AppUtilityService,
    private dialog: MatDialog,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.store.select(selectAppState)
      .pipe(takeUntil(this.destroy$))
      .subscribe(appState => {
        this.loggedInUserName = appState.loggedInUser ?? '';
        this.loggedInUserEmail = appState.loggedInUserEmail ?? '';
      });
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUsers(): void {
    const url = this.appUtilityService.getAPI(apiPaths.users.getUserInfo);
    this.appUtilityService.get<UsersResponse>(url)
      .pipe(take(1))
      .subscribe({
        next: response => {
          const users = response?.users ?? [];
          this.dataSource.data = users.filter(user => user.is_active);
          if (this.selectedUser) {
            this.selectedUser = this.dataSource.data.find(user => user.email === this.selectedUser?.email) ?? null;
          }
        },
        error: () => {
          this.dataSource.data = [];
          this.selectedUser = null;
        }
      });
  }

  selectUser(user: UserInfo): void {
    if (this.selectedUser?.email === user.email) {
      this.selectedUser = null;
      return;
    }
    this.selectedUser = user;
  }

  isSelected(user: UserInfo): boolean {
    return this.selectedUser?.email === user.email;
  }

  isSelfSelected(): boolean {
    if (!this.selectedUser) {
      return false;
    }
    const loggedEmail = (this.loggedInUserEmail ?? '').toLowerCase();
    const selectedEmail = (this.selectedUser.email ?? '').toLowerCase();
    if (loggedEmail && selectedEmail) {
      return loggedEmail === selectedEmail;
    }
    const loggedName = this.normalizeName(this.stripDisplaySuffix(this.loggedInUserName));
    if (!loggedName) {
      return false;
    }
    const firstLast = this.normalizeName(`${this.selectedUser.first_name} ${this.selectedUser.last_name}`);
    const lastFirst = this.normalizeName(`${this.selectedUser.last_name} ${this.selectedUser.first_name}`);
    return loggedName === firstLast || loggedName === lastFirst;
  }

  openAddUserDialog(): void {
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      autoFocus: false,
      panelClass: 'bpx-dialog-container',
      disableClose: true,
      width: '50vw'
    });

    dialogRef.afterClosed().subscribe((payload?: AddUserPayload) => {
      if (!payload) {
        return;
      }
      const url = this.appUtilityService.getAPI(apiPaths.users.createUser);
      this.appUtilityService.post<AddUserPayload, UserInfo>(url, payload)
        .pipe(take(1))
        .subscribe({
          next: () => this.loadUsers(),
          error: () => {
            this.loadUsers();
          }
        });
    });
  }

  openEditUserDialog(): void {
    if (!this.selectedUser) {
      return;
    }
    const dialogRef = this.dialog.open(EditUserDialogComponent, {
      autoFocus: false,
      panelClass: 'bpx-dialog-container',
      disableClose: true,
      data: {
        first_name: this.selectedUser.first_name,
        last_name: this.selectedUser.last_name,
        email: this.selectedUser.email,
        super_user: this.selectedUser.super_user
      }
    });

    dialogRef.afterClosed().subscribe((payload?: EditUserPayload) => {
      if (!payload || !this.selectedUser) {
        return;
      }
      const url = this.appUtilityService.getAPI(apiPaths.users.createUser);
      const request: AddUserPayload = {
        first_name: payload.first_name,
        last_name: payload.last_name,
        email: this.selectedUser.email,
        employee_id: this.resolveEmployeeId(this.selectedUser),
        job_title: this.selectedUser.job_title || 'TBD',
        azure_id: 'pending',
        super_user: payload.super_user,
        is_active: true
      };
      this.appUtilityService.post<AddUserPayload, UserInfo>(url, request)
        .pipe(take(1))
        .subscribe({
          next: () => this.loadUsers(),
          error: () => {
            this.loadUsers();
          }
        });
    });
  }

  // Prefer the backend value; otherwise derive it from {id}@global.bio-rad.com, else an empty string.
  private resolveEmployeeId(user: UserInfo): string {
    const fromBackend = user.employee_id?.trim();
    if (fromBackend) {
      return fromBackend;
    }
    const match = /^([^@\s]+)@global\.bio-rad\.com$/i.exec((user.email ?? '').trim());
    return match ? match[1] : '';
  }

  confirmDelete(): void {
    if (!this.selectedUser) {
      return;
    }
    const email = this.selectedUser.email;

    this.dialog.open(CommonDialogComponent, {
      panelClass: 'bpx-dialog-container',
      disableClose: true,
      data: {
        title: 'Delete User',
        message: `Are you sure you want to delete ${email}?`,
        okText: 'Delete',
        dismissText: 'Cancel',
        okAction: () => this.deactivateUser(email),
        dismissAction: () => null
      }
    });
  }

  private deactivateUser(email: string): void {
    const url = `${this.appUtilityService.getAPI(apiPaths.users.deactivateUser)}?email_to_deactivate=${encodeURIComponent(email)}`;
    this.appUtilityService.get<string>(url)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.selectedUser = null;
          this.loadUsers();
        },
        error: () => {
          this.selectedUser = null;
          this.loadUsers();
        }
      });
  }

  private normalizeName(value: string): string {
    return (value ?? '')
      .replace(/_/g, ' ')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  private stripDisplaySuffix(value: string): string {
    return (value ?? '').replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  }
}
